import type { CopyCatalog, CopyKey, CopyLang } from "./copyTypes";
import { DE_COPY } from "./de";
import { EN_COPY } from "./en";
import { ES_COPY } from "./es";
import { FI_COPY } from "./fi";
import { FR_COPY } from "./fr";
import { HR_COPY } from "./hr";
import { HU_COPY } from "./hu";
import { IT_COPY } from "./it";
import { NL_COPY } from "./nl";
import { SK_COPY } from "./sk";
import { SL_COPY } from "./sl";
import { ZH_COPY } from "./zh";
import type { PalletType, ServiceKey, SubmitErrorKind } from "../types";
import { COUNTRIES } from "../constants";

const CATALOGS: Record<CopyLang, CopyCatalog> = {
  en: EN_COPY,
  nl: NL_COPY,
  de: DE_COPY,
  es: ES_COPY,
  fi: FI_COPY,
  fr: FR_COPY,
  hr: HR_COPY,
  hu: HU_COPY,
  it: IT_COPY,
  sl: SL_COPY,
  sk: SK_COPY,
  zh: ZH_COPY,
};

const DISPLAY_LOCALES: Record<CopyLang, string> = {
  en: "en-US",
  nl: "nl-NL",
  de: "de-DE",
  es: "es-ES",
  fi: "fi-FI",
  fr: "fr-FR",
  hr: "hr-HR",
  hu: "hu-HU",
  it: "it-IT",
  sl: "sl-SI",
  sk: "sk-SK",
  zh: "zh-CN",
};

const PALLET_KEYS: Record<PalletType, CopyKey> = {
  euro: "pallet.euro",
  block: "pallet.block",
  ibc: "pallet.ibc",
  cp3: "pallet.cp3",
  half: "pallet.half",
  size80x60: "pallet.size80x60",
  size120x120: "pallet.size120x120",
  size150x80: "pallet.size150x80",
  custom: "pallet.custom",
  unspecified: "pallet.unspecified",
};

const SERVICE_PILL_KEYS: Record<ServiceKey, CopyKey> = {
  adr: "services.pill.adr",
  temp: "services.pill.temp",
  tail: "services.pill.tail",
  ftl: "services.pill.ftl",
  time: "services.pill.time",
  ref: "services.pill.ref",
  attachments: "services.pill.attachments",
};

const SUBMIT_ERROR_KEYS: Record<SubmitErrorKind, CopyKey> = {
  rate_limited: "result.error.rateLimited",
  forbidden: "result.error.forbidden",
  network: "result.error.network",
  too_large: "result.error.tooLarge",
  unsupported_type: "result.error.unsupportedType",
  email_unavailable: "result.error.emailUnavailable",
};

const ADR_CLASS_KEYS = [
  "services.adrClass1",
  "services.adrClass2",
  "services.adrClass3",
  "services.adrClass4",
  "services.adrClass5",
  "services.adrClass6",
  "services.adrClass7",
  "services.adrClass8",
  "services.adrClass9",
] as const;

export class QuoteCopy {
  private constructor(
    readonly language: CopyLang,
    private readonly catalog: CopyCatalog,
  ) {}

  static english(): QuoteCopy {
    return QuoteCopy.forLanguage("en");
  }

  static forLanguage(language: CopyLang): QuoteCopy {
    return new QuoteCopy(language, CATALOGS[language]);
  }

  static catalogs(): Record<CopyLang, CopyCatalog> {
    return CATALOGS;
  }

  t(key: CopyKey, vars?: Record<string, string | number>): string {
    let out: string = this.catalog[key];
    if (!vars) return out;
    for (const name of Object.keys(vars)) {
      out = out.split(`{${name}}`).join(String(vars[name]));
    }
    return out;
  }

  number(value: number): string {
    return value.toLocaleString(DISPLAY_LOCALES[this.language]);
  }

  countryName(code: string): string {
    if (!code) return "";
    const upper = code.trim().toUpperCase();
    try {
      const named = new Intl.DisplayNames([DISPLAY_LOCALES[this.language]], { type: "region" }).of(upper);
      if (named && named.toUpperCase() !== upper) return named;
    } catch {
      // Use the catalog name when the code is not a display region.
    }
    return COUNTRIES.find((country) => country.c === upper)?.n ?? upper;
  }

  palletLabel(type: PalletType): string {
    return this.t(PALLET_KEYS[type]);
  }

  servicePill(key: ServiceKey, references = false): string {
    if (key === "ref" && references) return this.t("services.pill.references");
    return this.t(SERVICE_PILL_KEYS[key]);
  }

  submitError(kind: SubmitErrorKind): string {
    return this.t(SUBMIT_ERROR_KEYS[kind]);
  }

  adrClassLabel(index: number): string {
    return this.t(ADR_CLASS_KEYS[index] ?? "services.selectClass");
  }

  stepLabels(): string[] {
    return [
      this.t("steps.route"),
      this.t("steps.shipment"),
      this.t("steps.services"),
      this.t("steps.contact"),
      this.t("steps.review"),
    ];
  }
}
